/**
 * Guided Navigation component Responsible for controlling flow of data and rendering children
 *
 * @module views/components/GuidedNavigation
 * @memberof -Common
 */

import './GuidedNavigation.scss';

import React, { useCallback, useMemo } from 'react';

import PropTypes from 'prop-types';

import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';

import PillCarousel, { PILL_TYPES } from '../PillCarousel/PillCarousel';
import ResultsHeader from '../ResultsHeader/ResultsHeader';
import * as utils from './GuidedNavigation';

/**
  * Represents a GuidedNavigation component
  *
  * @method
  * @param {GuidedNavigationProps} props - React properties passed from composition
  * @returns GuidedNavigation
  */

export const GuidedNavigation = function( props ){
  const { breakpoint } = useDeviceInflection();
  const { invokeAction, items, resultCountLabel, carouselAccessibility, previousAccessibility, nextAccessibility, carouselDetailsAccessibility, swiperType } = props;
  const displayArrows = breakpoint && breakpoint.CURRENT_BREAKPOINT !== 'SM';

  // Compose onClick for Guided Navigation pill
  // -> handles invoking the action from DXL
  const handleOnClick = useCallback( utils.composeOnClick( { invokeAction } ), [] );
  /**
   * Filtering out selected items from carouselItems when selected. Which will create a dismissible pill glued with applied filters.
   */
  const carouselItems = useMemo( () => {
    return items
      .filter( item => !item.selected )
      .map( item => {
        const selectAction = item.selected ? item.removeAction : item.applyAction;

        return {
          name: item.label,
          selected: item.selected,
          selectAction
        };
      } );
  }, [items] );

  return (
    <div className='GuidedNavigation'>
      <ResultsHeader
        resultCountLabel={ resultCountLabel }
      />
      <PillCarousel
        items={ carouselItems }
        displayArrows={ displayArrows }
        onPillClick={ handleOnClick }
        carouselAccessibility={ carouselAccessibility }
        previousAccessibility={ previousAccessibility }
        nextAccessibility={ nextAccessibility }
        carouselDetailsAccessibility={ carouselDetailsAccessibility }
        hasClearButton={ false }
        pillType={ PILL_TYPES.PILL }
        swiperType={ swiperType }
      />
    </div>
  );
};

/**
   * Method to handle invoking action from DXL for PillCarousel pills
   * @param {object} methods methods
   * @param {action} methods.invokeAction handler from layer host to invoke DXL action
   * @param {string} action DXL action
   */
export const composeOnClick = ( methods ) => ( action ) => {
  const { invokeAction } = methods || {};
  const { graphql, customHeaders, params, method } = action || {};

  if( !invokeAction || !action ){
    return;
  }

  invokeAction( {
    graphql,
    customHeaders,
    method,
    params
  } );
};

/**
  * Property type definitions
  * @typedef GuidedNavigationProps
  * @type {object}
  * @property {array} items - This is array of items
  * @property {string} resultCountLabel - Sets the result count label for the page
  * @property {function} invokeAction - Function from layerhost to invoke DXL action
  * @property {string} carouselAccessibility - This is the ADA text for carousel
  * @property {string} previousAccessibility - This is the ADA text for carousel prev pagination button
  * @property {string} nextAccessibility - This is the ADA text for carousel next pagination button
  */

export const propTypes =  {
  items: PropTypes.arrayOf( PropTypes.shape(
    {
      name: PropTypes.string,
      selected: PropTypes.bool,
      applyAction: PropTypes.shape( {
        label: PropTypes.string,
        url: PropTypes.string,
        graphql: PropTypes.string
      } ),
      removeAction: PropTypes.shape( {
        label: PropTypes.string,
        url: PropTypes.string,
        graphql: PropTypes.string
      } )
    }
  ) ),
  resultCountLabel: PropTypes.string,
  invokeAction: PropTypes.func,
  carouselAccessibility: PropTypes.string,
  previousAccessibility: PropTypes.string,
  nextAccessibility: PropTypes.string
};


GuidedNavigation.propTypes = propTypes;

export default GuidedNavigation;
